import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { CheckCircle, XCircle, Clock, Settings, Mail, RefreshCw, User } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PendingCard = ({ note, onAction }) => {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const ageDays = Math.floor((Date.now() - new Date(note.created_at)) / 86400000);

  const act = async (action, body) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/notes/${note.id}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Action failed'); return; }
      toast.success(action === 'approve' ? 'Note approved — submitter notified, now searchable in AI Chat' : 'Note declined — submitter notified with your reason');
      onAction();
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-amber-200 rounded-xl p-4" data-testid={`pending-note-${note.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-bold px-2 py-0.5 rounded uppercase bg-amber-100 text-amber-700">Pending</span>
        <span className="text-sm font-bold text-gray-800">{note.title}</span>
        {ageDays >= 1 && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${ageDays >= 5 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>{ageDays}d old</span>}
        {note.escalated_at && <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded">ESCALATED</span>}
      </div>
      <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px]">
        <span className="text-gray-500 flex items-center gap-0.5"><User size={9} /> {note.submitted_by_name}</span>
        <span className="text-blue-600 font-medium">{(note.carriers || []).join(', ')}</span>
        {note.state && <span className="text-green-600">{note.state}</span>}
        {note.lob && <span className="text-purple-600">{note.lob}</span>}
        {note.effective_date && <span className="text-gray-400">Eff: {note.effective_date}</span>}
        <span className="text-gray-400 ml-auto">{new Date(note.created_at).toLocaleString()}</span>
      </div>
      <p className="text-xs text-gray-700 mt-2 whitespace-pre-wrap bg-gray-50 rounded-lg p-2.5 border border-gray-100">{note.content}</p>
      {note.source_note && <p className="text-[10px] text-gray-400 mt-1">Source: {note.source_note}</p>}

      {!declining ? (
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={() => act('approve')} disabled={busy} className="h-8 text-xs bg-green-600 hover:bg-green-700 gap-1" data-testid={`approve-note-${note.id}`}>
            <CheckCircle size={12} /> Approve
          </Button>
          <Button size="sm" variant="outline" onClick={() => setDeclining(true)} disabled={busy} className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 gap-1" data-testid={`decline-note-${note.id}`}>
            <XCircle size={12} /> Decline
          </Button>
        </div>
      ) : (
        <div className="mt-3 bg-red-50/50 border border-red-100 rounded-lg p-3">
          <label className="text-[10px] font-semibold text-red-600 block mb-1">Reason for declining * (required — the submitter will see this)</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} maxLength={1000}
            placeholder="e.g., This conflicts with the official manual — please verify with the underwriter and resubmit with the email attached."
            className="w-full text-xs p-2 border border-red-200 rounded-lg h-16 resize-none bg-white" data-testid={`decline-reason-input-${note.id}`} autoFocus />
          <div className="flex gap-2 mt-2">
            <Button size="sm" onClick={() => act('decline', { reason })} disabled={busy || reason.trim().length < 5}
              className="h-7 text-xs bg-red-600 hover:bg-red-700" data-testid={`decline-confirm-${note.id}`}>
              Confirm Decline
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setDeclining(false); setReason(''); }} className="h-7 text-xs">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsCard = ({ users, onSaved }) => {
  const [form, setForm] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/settings`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json()).then(setForm).catch(() => {});
  }, []);

  const save = async () => {
    const res = await fetch(`${API}/api/settings`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ approver_user_id: form.approver_user_id, pending_timeout_days: form.pending_timeout_days, super_admin_email: form.super_admin_email }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data.detail || 'Save failed'); return; }
    toast.success('Approval settings saved');
    fetch(`${API}/api/settings`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json()).then(setForm).catch(() => {});
    onSaved();
  };

  if (!form) return null;
  return (
    <Card className="p-4 border-gray-200" data-testid="approval-settings-card">
      <h3 className="text-xs font-bold text-gray-700 mb-3 flex items-center gap-1"><Settings size={12} /> Approval Settings</h3>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] font-semibold text-gray-500 block mb-1">Designated Approver</label>
          <select value={form.approver_user_id} onChange={e => setForm(f => ({ ...f, approver_user_id: e.target.value }))}
            className="w-full h-8 text-xs border rounded-md px-2 bg-white" data-testid="settings-approver">
            {users.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-gray-500 block mb-1">Escalate after (days pending)</label>
          <Input type="number" min="1" max="90" value={form.pending_timeout_days}
            onChange={e => setForm(f => ({ ...f, pending_timeout_days: e.target.value }))} className="h-8 text-xs" data-testid="settings-timeout" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-gray-500 block mb-1">Super Admin Email (escalations)</label>
          <Input type="email" value={form.super_admin_email}
            onChange={e => setForm(f => ({ ...f, super_admin_email: e.target.value }))} className="h-8 text-xs" data-testid="settings-superadmin" />
        </div>
      </div>
      <Button size="sm" onClick={save} className="h-7 text-xs bg-[#101C47] mt-3" data-testid="settings-save">Save Settings</Button>
    </Card>
  );
};

const OutboxLog = ({ headers }) => {
  const [emails, setEmails] = useState([]);
  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/emails/outbox`, { headers: headers() });
    if (res.ok) setEmails((await res.json()).emails || []);
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const badge = (s) => s === 'sent' ? 'bg-green-100 text-green-700' : s === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700';
  return (
    <Card className="p-4 border-gray-200" data-testid="email-outbox-log">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-bold text-gray-700 flex items-center gap-1"><Mail size={12} /> Email Delivery Log <span className="text-gray-400 font-normal">(fail-proof outbox — auto-retries up to 10x)</span></h3>
        <button onClick={load} className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-1"><RefreshCw size={9} /> Refresh</button>
      </div>
      {emails.length === 0 && <p className="text-[11px] text-gray-400">No emails sent yet</p>}
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {emails.slice(0, 30).map(e => (
          <div key={e.id} className="flex items-center gap-2 text-[10px] border-b border-gray-50 pb-1 last:border-0">
            <span className={`font-bold px-1.5 py-0.5 rounded uppercase ${badge(e.status)}`}>{e.status}</span>
            <span className="text-gray-500 w-32 truncate">{Array.isArray(e.to) ? e.to.join(', ') : e.to}</span>
            <span className="text-gray-700 flex-1 truncate">{e.subject}</span>
            <span className="text-gray-400">{e.category}</span>
            {e.attempts > 1 && <span className="text-amber-600 font-bold">{e.attempts} tries</span>}
            {e.last_error && e.status !== 'sent' && <span className="text-red-400 truncate max-w-[140px]" title={e.last_error}>{e.last_error}</span>}
            <span className="text-gray-300">{new Date(e.created_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

export const ApprovalsTab = ({ headers, isSuperAdmin, users, onChanged }) => {
  const [notes, setNotes] = useState([]);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/notes`, { headers: headers() });
    if (res.ok) setNotes((await res.json()).notes || []);
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const pending = notes.filter(n => n.status === 'pending');
  const history = notes.filter(n => n.status !== 'pending');
  const refresh = () => { load(); onChanged(); };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="max-w-4xl mx-auto space-y-4">
        {isSuperAdmin && <SettingsCard users={users} onSaved={refresh} />}

        <div>
          <h2 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-1.5">
            <Clock size={14} className="text-amber-500" /> Pending Approval
            {pending.length > 0 && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{pending.length}</span>}
          </h2>
          {pending.length === 0 && (
            <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
              <CheckCircle size={28} className="mx-auto text-green-300 mb-2" />
              <p className="text-gray-400 text-sm">All caught up — nothing pending</p>
            </div>
          )}
          <div className="space-y-2" data-testid="pending-notes-list">
            {pending.map(n => <PendingCard key={n.id} note={n} onAction={refresh} />)}
          </div>
        </div>

        {history.length > 0 && (
          <div>
            <h2 className="text-sm font-bold text-gray-800 mb-2">History</h2>
            <div className="space-y-1" data-testid="notes-history-list">
              {history.map(n => (
                <div key={n.id} className="flex items-center gap-2 bg-white border border-gray-100 rounded-lg px-3 py-2 text-xs">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${n.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{n.status}</span>
                  <span className="font-medium text-gray-800 flex-1 truncate">{n.title}</span>
                  <span className="text-blue-600 text-[10px] truncate max-w-[160px]">{(n.carriers || []).join(', ')}</span>
                  <span className="text-gray-400 text-[10px]">by {n.submitted_by_name}</span>
                  <span className="text-gray-300 text-[10px]">{n.reviewed_by_name ? `→ ${n.reviewed_by_name}` : ''}</span>
                  <span className="text-gray-300 text-[10px]">{new Date(n.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {isSuperAdmin && <OutboxLog headers={headers} />}
      </div>
    </div>
  );
};
